import './Landingpage.scss';

import { ThemeProvider, createTheme } from '@mui/material';
import { deDE } from '@mui/material/locale';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { faFacebookF, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { Check, Flame, Heart, Lock, LogIn, Mail, ShieldCheck, Users, VenetianMask, Zap } from 'lucide-react';

import Config from 'config';
import Icon from 'components/Icon';
import LoginModal from 'components/NewRegister/Login';
import ForgotPasswordModal from 'components/ForgotPasswordModal';

import bg from 'assets/images/landingpage/bg.jpg';
import topMockup from 'assets/images/landingpage/top_mockup.png';
import profil1 from 'assets/images/landingpage/profil_1.png';
import profil2 from 'assets/images/landingpage/profil_2.png';
import profil3 from 'assets/images/landingpage/profil_3.png';
import profil4 from 'assets/images/landingpage/profil_4.png';
import { FORGOT_PASSWORD_PATH, FORGOT_PASSWORD_PATHH, REGISTER_USER } from '../../models/Paths';
import { DefaultTheme } from '../../theme/DefaultTheme';
import { FacebookService } from '../../services/Facebook/FacebookService';
import { GoogleService } from '../../services/Google/GoogleService';
import useLoginHandler from '../../services/Facebook/useLoginHandler';
import useGoogleLoginHandler from '../../services/Google/useGoogleLoginHandler';

export interface ILandingpageProps {}

const features = [
    {
        icon: Heart,
        image: profil2,
        title: 'Finde, was zu dir passt',
        text: 'Entdecke Profile, die deine Vorlieben teilen und offen fuer das sind, was du suchst.',
    },
    {
        icon: Flame,
        image: profil3,
        title: 'Heisse Chats, echte Kontakte',
        text: 'Schreibe private Nachrichten und erlebe aufregende Unterhaltungen.',
    },
    {
        icon: Lock,
        title: 'Sicher, diskret und anonym',
        text: 'Deine Daten bleiben geschuetzt und deine Aktivitaeten vertraulich.',
    },
];

const trustItems = [
    { icon: VenetianMask, title: '100% Diskretion', text: 'Keine oeffentlichen Profile, keine Sichtbarkeit im Web.' },
    { icon: ShieldCheck, title: 'Deine Sicherheit', text: 'Moderne Sicherheitsstandards schuetzen deine Daten.' },
    { icon: Users, title: 'Fuer Erwachsene', text: 'Nur fuer volljaehrige Personen. Respektvoller Umgang garantiert.' },
    { icon: Flame, title: 'Echte Menschen', text: 'Manuelle Pruefung fuer echte und aktive Profile.' },
];

export const Landingpage = React.memo((_props: ILandingpageProps) => {
    const history = useHistory();
    const forgotPasswordRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATH, exact: true });
    const forgotPasswordLegacyRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATHH, exact: true });
    const isForgotPasswordOpen = Boolean(forgotPasswordRoute || forgotPasswordLegacyRoute);

    const [loginOpen, setLoginOpen] = useState(false);
    const [, setLoading] = useState(false);

    const handleFBLogin = useLoginHandler();
    const handleGoogleLogin = useGoogleLoginHandler();
    const domain = Config.GLOBAL_SITE_DOMAIN || `${Config.GLOBAL_SITE_NAME.toLowerCase()}.de`;
    const [brandWord, brandTld = 'de'] = domain.split('.');

    useEffect(() => {
        FacebookService.init();
        GoogleService.init();
    }, []);

    const handleGoogleClick = useCallback(() => {
        setLoading(true);
        try {
            GoogleService.login(handleGoogleLogin);
        } finally {
            setLoading(false);
        }
    }, [handleGoogleLogin]);

    const handleFacebookClick = useCallback(() => {
        setLoading(true);
        FacebookService.login(async (response) => {
            if (response.status === 'connected') {
                await handleFBLogin(response);
            } else {
                FacebookService.logout();
            }
            setLoading(false);
        });
    }, [handleFBLogin]);

    const handleEmailRegister = useCallback(() => history.push(REGISTER_USER), [history]);
    const openLogin = useCallback(() => setLoginOpen(true), []);

    return (
        <ThemeProvider theme={createTheme(DefaultTheme, deDE)}>
            <div className="pl">
                <section className="pl__hero" style={{ backgroundImage: `url(${bg})` }}>
                    <header className="pl__header">
                        <div className="pl__brand" aria-label={domain}>
                            <span className="pl__brand-word">{brandWord}</span>
                            <span className="pl__brand-tld">.{brandTld}</span>
                        </div>

                        <nav className="pl__nav" aria-label="Account">
                            <button type="button" className="pl__nav-btn pl__nav-btn--ghost" onClick={openLogin}>
                                <LogIn />
                                Einloggen
                            </button>
                            <button type="button" className="pl__nav-btn pl__nav-btn--primary" onClick={handleEmailRegister}>
                                Jetzt registrieren
                            </button>
                        </nav>
                    </header>

                    <div className="pl__hero-inner">
                        <div className="pl__hero-copy">
                            <h1 className="pl__hero-title">
                                Finde, was dich
                                <span>wirklich bewegt</span>
                            </h1>
                            <p className="pl__hero-sub">
                                {domain} ist der Treffpunkt fuer erwachsene Menschen, die offen fuer echte Begegnungen und spannende Nachrichten sind.
                            </p>

                            <div className="pl__hero-benefits">
                                <span>
                                    <VenetianMask /> Diskret &amp; sicher
                                </span>
                                <span>
                                    <Heart /> Echte Profile
                                </span>
                                <span>
                                    <Zap /> Schnell &amp; einfach
                                </span>
                            </div>

                            <button type="button" className="pl__hero-cta" onClick={handleEmailRegister}>
                                Kostenlos anmelden
                            </button>
                        </div>
                    </div>
                </section>

                <main className="pl__main">
                    <section className="pl__auth-card">
                        <p className="pl__auth-title">Schnell anmelden mit</p>
                        <div className="pl__auth-actions">
                            <button type="button" className="pl__auth-btn pl__auth-btn--google" onClick={handleGoogleClick}>
                                <Icon icon={faGoogle} />
                                Google
                            </button>
                            <button type="button" className="pl__auth-btn pl__auth-btn--facebook" onClick={handleFacebookClick}>
                                <Icon icon={faFacebookF} />
                                Facebook
                            </button>
                            <button type="button" className="pl__auth-btn pl__auth-btn--email" onClick={handleEmailRegister}>
                                <Mail />
                                E-Mail
                            </button>
                        </div>
                        <button type="button" className="pl__auth-login" onClick={openLogin}>
                            Bereits Mitglied? <span>Jetzt einloggen</span>
                        </button>
                    </section>

                    <section className="pl__features">
                        {features.map((feature) => {
                            const FeatureIcon = feature.icon;
                            return (
                                <div key={feature.title} className="pl__feature">
                                    <span className="pl__feature-icon">
                                        <FeatureIcon />
                                    </span>
                                    <div className="pl__feature-copy">
                                        <h2>{feature.title}</h2>
                                        <p>{feature.text}</p>
                                    </div>
                                    {feature.image ? <img src={feature.image} alt="" loading="lazy" /> : <span className="pl__shield-art"><Lock /></span>}
                                </div>
                            );
                        })}
                    </section>

                    <section className="pl__experience">
                        <div className="pl__phone">
                            <img src={topMockup} alt="" loading="lazy" />
                            <div className="pl__chat-screen">
                                <div className="pl__chat-head">
                                    <img src={profil4} alt="" />
                                    <span>
                                        Lena
                                        <small>Online</small>
                                    </span>
                                </div>
                                <img className="pl__chat-photo" src={profil1} alt="" />
                                <p className="pl__bubble pl__bubble--light">Hey du, Lust auf ein gutes Gespraech?</p>
                                <p className="pl__bubble pl__bubble--hot">Na klar! Woran denkst du gerade?</p>
                            </div>
                        </div>

                        <div className="pl__experience-copy">
                            <p className="pl__eyebrow">Live Flirt Chat</p>
                            <h2>Erlebe echte Spannung</h2>
                            <p className="pl__experience-sub">
                                Unsere Community ist aktiv und wartet auf dich. Starte jetzt deinen ersten Flirt!
                            </p>
                            <ul>
                                <li>
                                    <Check /> Aktive Mitglieder in deiner Naehe
                                </li>
                                <li>
                                    <Check /> Private Fotos &amp; exklusive Inhalte
                                </li>
                                <li>
                                    <Check /> Direkt loslegen ohne lange Wartezeit
                                </li>
                                <li>
                                    <Check /> Fuer alle Vorlieben &amp; Fantasien
                                </li>
                            </ul>
                        </div>

                        <div className="pl__seal">
                            <span>
                                <Check />
                            </span>
                            <strong>Verifizierte Profile</strong>
                            <p>Nur echte Mitglieder fuer echte Erlebnisse.</p>
                        </div>
                    </section>

                    <section className="pl__trust">
                        {trustItems.map((item) => {
                            const TrustIcon = item.icon;
                            return (
                                <div className="pl__trust-item" key={item.title}>
                                    <TrustIcon />
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                </div>
                            );
                        })}
                    </section>
                </main>

                <footer className="pl__footer">
                    <nav className="pl__footer-links" aria-label="Rechtliches">
                        <Link to="/support">Kontakt</Link>
                        <Link to="/impressum">Impressum</Link>
                        <Link to="/datenschutz">Datenschutz</Link>
                        <Link to="/agb">AGB</Link>
                        <Link to="/content-guidelines">Content &amp; Community Guidelines</Link>
                    </nav>
                    <p className="pl__footer-legal">
                        Diese Webseite darf ausschlie&szlig;lich von Personen genutzt werden, die mindestens 18 Jahre alt sind. Die Plattform stellt
                        einen virtuellen Chat-Dienst f&uuml;r unterhaltsame, flirtende und fantasievolle Gespr&auml;che zur Verf&uuml;gung. Hierbei
                        werden fiktive Profile verwendet, mit denen keine pers&ouml;nlichen oder realen Treffen m&ouml;glich sind. Die Kosten je
                        Nachricht k&ouml;nnen je nach gew&auml;hlter Zahlungsart unterschiedlich ausfallen. Bitte informieren Sie sich vor der Nutzung
                        der Webseite &uuml;ber die geltenden Nutzungsbedingungen und Datenschutzbestimmungen.
                    </p>
                </footer>

                <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
                <ForgotPasswordModal open={isForgotPasswordOpen} />
            </div>
        </ThemeProvider>
    );
});

export default Landingpage;
